/**
 * Recommended rail hydrator for homepage-style personalized suggestions.
 *
 * Keeps the standard collection-card UI, pulls from the Garden
 * personalized-home v2 engine, widens the local pool, and rotates the
 * visible 10-card slice on refresh without paying for a fresh network request
 * every click.
 */
(function () {
  'use strict';

  if (window.__mmRecommendedSectionInit) return;
  window.__mmRecommendedSectionInit = true;

  var CONTAINER_SELECTOR = '[data-trending-container]';
  var REFRESH_SELECTOR = '[data-mm-home-rec-refresh]';
  var RECOMMENDATIONS_ENDPOINT = '/apps/garden/v1/recommendations';
  var TOP_PRODUCTS_ENDPOINT = '/apps/garden/v1/demand/top-products?availability=available';
  var NEWEST_PRODUCTS_ENDPOINT = '/collections/all/products.json?sort_by=created-descending';
  var CATALOG_PRODUCTS_ENDPOINT = '/collections/all/products.json';
  var DISPLAY_LIMIT = 10;
  var MIN_DISPLAY_COUNT = 5;
  var POOL_LIMIT = 20;
  var CACHE_TTL_MS = 60 * 60 * 1000;
  // Hybrid eligibility is reused briefly in this page only; never persist decisions.
  var HYBRID_CACHE_TTL_MS = 60 * 1000;
  var CACHE_KEY_PREFIX = 'v3:mrmaple:recommended-rail:v4';
  var SKELETON_COUNT = 4;
  var INTERSECTION_ROOT_MARGIN = '1200px 0px';
  var CARD_MISS_CACHE_PREFIX = 'mrmaple:trending-card-miss:';
  var CARD_MISS_TTL_MS = 6 * 60 * 60 * 1000;
  var CATALOG_RANDOM_PAGE_MIN = 2;
  var CATALOG_RANDOM_PAGE_MAX = 24;
  var CATALOG_RANDOM_PAGE_SIZE = 50;
  var CATALOG_RANDOM_ATTEMPTS = 3;

  var currentEntry = null;
  var strictEligibility = false;
  var currentDisplayHandles = [];
  var currentRotation = 0;
  var hydrationPromise = null;

  function buildSkeletonCards(count) {
    var html = '';
    for (var i = 0; i < count; i += 1) {
      html +=
        '<div class="neu-card neu-card--skeleton" aria-hidden="true">' +
        '<div class="neu-card__image-wrap"></div>' +
        '<div class="neu-card__info">' +
        '<div class="neu-card__title">&nbsp;</div>' +
        '<div class="neu-card__price">&nbsp;</div>' +
        '</div>' +
        '</div>';
    }
    return html;
  }

  function preserveFallbackMarkup(container) {
    if (!container || typeof container.__mmRecommendedFallbackMarkup === 'string') {
      return;
    }

    container.__mmRecommendedFallbackMarkup = container.querySelector('.productgrid--item') ? container.innerHTML : '';
  }

  function renderFallbackDisplay(container) {
    if (strictEligibility) { if (container) container.innerHTML = ""; return false; }
    if (!container) return false;

    preserveFallbackMarkup(container);

    var fallbackMarkup = container.__mmRecommendedFallbackMarkup || '';
    if (!fallbackMarkup) return false;

    container.innerHTML = fallbackMarkup;
    shuffleFallbackDisplay(container);

    var cards = Array.prototype.slice.call(container.querySelectorAll('.productgrid--item'));
    for (var cardIndex = cards.length - 1; cardIndex >= 0; cardIndex -= 1) {
      var availability = String(cards[cardIndex].getAttribute('data-product-available') || '')
        .trim()
        .toLowerCase();
      if (availability === 'false') {
        cards[cardIndex].remove();
      }
    }

    cards = Array.prototype.slice.call(container.querySelectorAll('.productgrid--item'));
    if (cards.length < MIN_DISPLAY_COUNT) {
      container.innerHTML = '';
      return false;
    }

    var variant = getRecommendationVariant(container);
    for (var i = 0; i < cards.length; i += 1) {
      cards[i].classList.add('neu-trending-card');
      cards[i].setAttribute('data-mm-rec-surface', variant);
      if (!cards[i].getAttribute('data-rec-source')) {
        cards[i].setAttribute('data-rec-source', 'theme-fallback');
      }
    }

    if (typeof CustomEvent === 'function') {
      container.dispatchEvent(new CustomEvent('mm:cards:added', { bubbles: true }));
      container.dispatchEvent(
        new CustomEvent('theme:rimg:watch', {
          bubbles: true,
          cancelable: true,
        }),
      );
    }

    if (window.rimg && typeof window.rimg.watch === 'function') {
      try {
        window.rimg.watch(container);
      } catch (_) {}
    }

    return true;
  }

  function shuffleFallbackDisplay(container) {
    if (!container) return;

    var cards = Array.prototype.slice.call(container.querySelectorAll('.productgrid--item'));
    if (cards.length <= 1) return;

    var originalFirst = cards[0];
    var seed = [getCustomerKey(), getCustomerZone() || 'no-zone', currentRotation, Math.floor(Date.now() / CACHE_TTL_MS), 'theme-fallback'].join(':');
    var shuffled = shuffleWithSeed(cards, seed);

    if (shuffled[0] === originalFirst && shuffled.length > 1) {
      shuffled.push(shuffled.shift());
    }

    container.innerHTML = '';
    shuffled.slice(0, Math.max(DISPLAY_LIMIT, MIN_DISPLAY_COUNT)).forEach(function (card) {
      container.appendChild(card);
    });
  }

  function getCustomerKey() {
    if (window.ShopifyCustomer && window.ShopifyCustomer.id) {
      return String(window.ShopifyCustomer.id);
    }

    if (window.ShopifyAnalytics && window.ShopifyAnalytics.meta && window.ShopifyAnalytics.meta.customerId) {
      return String(window.ShopifyAnalytics.meta.customerId);
    }

    return 'anon';
  }

  function getCustomerZone() {
    if (window.MMCustomerZone) {
      return String(window.MMCustomerZone).trim().toLowerCase();
    }

    try {
      var stored = localStorage.getItem('usdaZone') || localStorage.getItem('mrmaple_zone') || localStorage.getItem('mrmaple:customer:zone') || '';
      return String(stored || '')
        .trim()
        .toLowerCase();
    } catch (_) {
      return '';
    }
  }

  function getRecommendationVariant(container) {
    if (!container) return 'home';

    var variant = String(container.getAttribute('data-mm-recommendation-variant') || '')
      .trim()
      .toLowerCase();
    if (variant === 'pdp' || variant === 'cart' || variant === 'contextual') {
      return variant;
    }

    return /^\/products\//.test(location.pathname) ? 'pdp' : 'home';
  }

  function getCacheKey() {
    return [CACHE_KEY_PREFIX, location.pathname, window.MrMapleRecommendations ? window.MrMapleRecommendations.cacheKey() : "untracked", getCustomerKey(), getCustomerZone() || 'no-zone', DISPLAY_LIMIT, POOL_LIMIT].join(':');
  }

  function readPersistentCache() {
    var cacheKey = getCacheKey();

    if (currentEntry && currentEntry.cacheKey === cacheKey) {
      return currentEntry;
    }

    try {
      var raw = localStorage.getItem(cacheKey);
      if (!raw) return null;

      var parsed = JSON.parse(raw);
      if (!parsed || parsed.cacheKey !== cacheKey) {
        return null;
      }

      currentEntry = parsed;
      return parsed;
    } catch (_) {
      try {
        localStorage.removeItem(cacheKey);
      } catch (_) {}
      return null;
    }
  }

  function clearPersistentCache() {
    var cacheKey = getCacheKey();
    currentEntry = null;
    currentDisplayHandles = [];

    try {
      localStorage.removeItem(cacheKey);
    } catch (_) {}
  }

  function writePersistentCache(pool, cardsByHandle) {
    var now = Date.now();
    var entry = {
      cacheKey: getCacheKey(),
      fetchedAt: now,
      expiresAt: now + (strictEligibility ? HYBRID_CACHE_TTL_MS : CACHE_TTL_MS),
      pool: Array.isArray(pool) ? pool : [],
      cardsByHandle: cardsByHandle && typeof cardsByHandle === 'object' ? cardsByHandle : {},
    };

    currentEntry = entry;

    try {
      if (!strictEligibility) localStorage.setItem(entry.cacheKey, JSON.stringify(entry));
    } catch (_) {}

    return entry;
  }

  function getCachedEntry() {
    var entry = readPersistentCache();
    if (!entry) return null;

    if (typeof entry.expiresAt !== 'number' || entry.expiresAt <= Date.now()) {
      clearPersistentCache();
      return null;
    }

    if (!Array.isArray(entry.pool)) {
      clearPersistentCache();
      return null;
    }

    if (!entry.cardsByHandle || typeof entry.cardsByHandle !== 'object') {
      entry.cardsByHandle = {};
    }

    return entry;
  }

  function isValidProductHandle(handle) {
    var normalized = String(handle || '').trim();
    return !!normalized && normalized !== 'null' && normalized !== 'undefined';
  }

  function normalizeProductUrl(url, handle) {
    var normalized = String(url || '').trim();
    if (normalized) return normalized;
    return handle ? '/products/' + encodeURIComponent(handle) : '';
  }

  function getCardMissCacheKey(handle) {
    return CARD_MISS_CACHE_PREFIX + String(handle || '').trim();
  }

  function hasRecentCardMiss(handle) {
    try {
      var raw = sessionStorage.getItem(getCardMissCacheKey(handle));
      if (!raw) return false;
      var timestamp = Number.parseInt(raw, 10);
      if (!Number.isFinite(timestamp) || Date.now() - timestamp > CARD_MISS_TTL_MS) {
        sessionStorage.removeItem(getCardMissCacheKey(handle));
        return false;
      }
      return true;
    } catch (_) {
      return false;
    }
  }

  function rememberCardMiss(handle) {
    try {
      sessionStorage.setItem(getCardMissCacheKey(handle), String(Date.now()));
    } catch (_) {}
  }

  function normalizeAvailability(item) {
    if (!item || typeof item !== 'object') return null;
    if (typeof item.available === 'boolean') {
      return item.available;
    }
    if (typeof item.availableForSale === 'boolean') {
      return item.availableForSale;
    }
    if (Array.isArray(item.variants) && item.variants.length > 0) {
      var sawKnownAvailability = false;
      for (var index = 0; index < item.variants.length; index += 1) {
        var variant = item.variants[index];
        if (!variant || typeof variant !== 'object') continue;
        if (typeof variant.available === 'boolean') {
          sawKnownAvailability = true;
          if (variant.available) return true;
        }
        if (typeof variant.availableForSale === 'boolean') {
          sawKnownAvailability = true;
          if (variant.availableForSale) return true;
        }
      }
      if (sawKnownAvailability) return false;
    }
    return null;
  }

  function normalizeRecommendedHandle(item) {
    if (!item || typeof item !== 'object') return '';

    if (item.handle) {
      var handle = String(item.handle).trim();
      return isValidProductHandle(handle) ? handle : '';
    }

    var rawUrl = String(item.url || item.productUrl || item.href || '').trim();
    if (!rawUrl) return '';

    var match = rawUrl.match(/\/products\/([^/?#]+)/i);
    if (!match) return '';

    try {
      var decoded = decodeURIComponent(match[1]).trim();
      return isValidProductHandle(decoded) ? decoded : '';
    } catch (_) {
      var fallback = String(match[1]).trim();
      return isValidProductHandle(fallback) ? fallback : '';
    }
  }

  function normalizeRecommendationItem(item, source) {
    if (!item || typeof item !== 'object') return null;

    var handle = normalizeRecommendedHandle(item);
    var productId = String(item.productId || item.productGid || item.id || '').trim();
    var available = normalizeAvailability(item);
    if (!handle) return null;
    if (available === false) return null;

    return {
      source: source,
      decisionToken: item.decisionToken || null,
      algorithmVersion: item.algorithmVersion || null,
      handle: handle,
      productId: productId,
      available: available,
      reason: String(item.reason || '').trim(),
      reasonSource: String(item.reasonSource || '').trim(),
      url: normalizeProductUrl(item.url, handle),
    };
  }

  function dedupeProducts(products) {
    var seen = Object.create(null);
    var output = [];

    for (var i = 0; i < (products || []).length; i += 1) {
      var product = products[i];
      if (!product || !isValidProductHandle(product.handle)) continue;
      if (product.available === false) continue;
      if (seen[product.handle]) continue;
      seen[product.handle] = true;
      output.push(product);
    }

    return output;
  }

  async function fetchPersonalizedProducts(limit) {
    var url = new URL(RECOMMENDATIONS_ENDPOINT, window.location.origin);
    url.searchParams.set('strategy', 'personalized-home');
    url.searchParams.set('engine', 'v2');
    url.searchParams.set('limit', String(limit));

    var zone = getCustomerZone();
    if (zone) {
      url.searchParams.set('zone', zone);
    }

    try {
      var requestUrl = url.pathname === RECOMMENDATIONS_ENDPOINT && window.MrMapleRecommendations ? await window.MrMapleRecommendations.url(url.toString()) : url.toString();
      var response = await (window.MrMapleRecommendations ? window.MrMapleRecommendations.fetch : window.fetch)(requestUrl, {
        credentials: 'same-origin',
        headers: { Accept: 'application/json' },
      });

      if (!response.ok) {
        throw new Error('Status ' + response.status);
      }

      var payload = await response.json();
      strictEligibility = Boolean(payload && /^hybrid-v3/.test(payload.engine || ""));
      var recommendations = Array.isArray(payload && payload.recommendations) ? payload.recommendations : [];
      var fallbacks = Array.isArray(payload && payload.fallbacks) ? payload.fallbacks : [];

      return recommendations
        .map(function (item) {
          return normalizeRecommendationItem(item, 'personalized');
        })
        .concat(
          fallbacks.map(function (item) {
            return normalizeRecommendationItem(item, 'snapshot-fallback');
          }),
        )
        .filter(Boolean);
    } catch (error) {
      console.debug('[Recommended] Personalized recommendations unavailable:', error);
      return [];
    }
  }

  async function fetchDemandProducts(limit) {
    try {
      var response = await fetch(TOP_PRODUCTS_ENDPOINT + '&limit=' + encodeURIComponent(String(limit)), {
        credentials: 'same-origin',
        headers: { Accept: 'application/json' },
      });

      if (!response.ok) {
        throw new Error('Status ' + response.status);
      }

      var payload = await response.json();
      var products = Array.isArray(payload && payload.products) ? payload.products : [];
      return products
        .map(function (item) {
          return normalizeRecommendationItem(item, 'demand');
        })
        .filter(Boolean);
    } catch (error) {
      console.debug('[Recommended] Demand fallback failed:', error);
      return [];
    }
  }

  async function fetchNewestProducts(limit) {
    try {
      var response = await fetch(NEWEST_PRODUCTS_ENDPOINT + '&limit=' + encodeURIComponent(String(limit)), {
        credentials: 'same-origin',
        headers: { Accept: 'application/json' },
      });

      if (!response.ok) {
        throw new Error('Status ' + response.status);
      }

      var payload = await response.json();
      var products = Array.isArray(payload && payload.products) ? payload.products : [];
      return products
        .map(function (item) {
          return normalizeRecommendationItem(item, 'newest');
        })
        .filter(Boolean);
    } catch (error) {
      console.debug('[Recommended] Catalog fallback failed:', error);
      return [];
    }
  }

  function getCatalogPageSeed() {
    return [getCustomerKey(), getCustomerZone() || 'no-zone', currentRotation, Math.floor(Date.now() / CACHE_TTL_MS), 'catalog-random'].join(':');
  }

  function getRandomCatalogPages() {
    var span = CATALOG_RANDOM_PAGE_MAX - CATALOG_RANDOM_PAGE_MIN + 1;
    var base = hashString(getCatalogPageSeed()) || 1;
    var pages = [];
    var offsets = [0, 7, 15, 21];

    for (var index = 0; index < offsets.length && pages.length < CATALOG_RANDOM_ATTEMPTS; index += 1) {
      var page = CATALOG_RANDOM_PAGE_MIN + ((base + offsets[index]) % span);
      if (pages.indexOf(page) === -1) {
        pages.push(page);
      }
    }

    return pages;
  }

  function buildCatalogPageUrl(page, limit) {
    var url = new URL(CATALOG_PRODUCTS_ENDPOINT, window.location.origin);
    url.searchParams.set('limit', String(limit));
    url.searchParams.set('page', String(page));
    return url.toString();
  }

  async function fetchCatalogPageProducts(page, limit) {
    try {
      var response = await fetch(buildCatalogPageUrl(page, limit), {
        credentials: 'same-origin',
        headers: { Accept: 'application/json' },
      });

      if (!response.ok) {
        throw new Error('Status ' + response.status);
      }

      var payload = await response.json();
      var products = Array.isArray(payload && payload.products) ? payload.products : [];
      return products
        .map(function (item) {
          return normalizeRecommendationItem(item, 'catalog-random');
        })
        .filter(Boolean);
    } catch (error) {
      console.debug('[Recommended] Random catalog fallback failed:', error);
      return [];
    }
  }

  async function fetchRandomCatalogProducts(limit) {
    var pages = getRandomCatalogPages();
    var products = [];

    for (var index = 0; index < pages.length; index += 1) {
      products = dedupeProducts(products.concat(await fetchCatalogPageProducts(pages[index], CATALOG_RANDOM_PAGE_SIZE))).slice(0, limit);

      if (products.length >= MIN_DISPLAY_COUNT) {
        break;
      }
    }

    return shuffleWithSeed(products, getCatalogPageSeed()).slice(0, limit);
  }

  async function fetchRecommendedPool() {
    var pool = [];

    function merge(items) {
      pool = dedupeProducts(pool.concat(items || [])).slice(0, POOL_LIMIT);
    }

    merge(await fetchPersonalizedProducts(POOL_LIMIT));
    if (strictEligibility) return pool;

    if (pool.length < DISPLAY_LIMIT) {
      var fallbacks = await Promise.all([fetchDemandProducts(POOL_LIMIT), fetchNewestProducts(POOL_LIMIT)]);
      merge(fallbacks[0]);
      merge(fallbacks[1]);
    }

    if (pool.length < DISPLAY_LIMIT) {
      merge(await fetchRandomCatalogProducts(POOL_LIMIT));
    }

    return pool.slice(0, POOL_LIMIT);
  }

  async function recoverShortPoolWithCatalog() {
    if (strictEligibility) return false;
    var catalogProducts = await fetchRandomCatalogProducts(POOL_LIMIT);
    if (catalogProducts.length === 0) return false;

    var existingPool = currentEntry && Array.isArray(currentEntry.pool) ? currentEntry.pool : [];
    var cardsByHandle = currentEntry && currentEntry.cardsByHandle && typeof currentEntry.cardsByHandle === 'object' ? currentEntry.cardsByHandle : {};
    var mergedPool = dedupeProducts(existingPool.concat(catalogProducts)).slice(0, POOL_LIMIT);
    if (mergedPool.length < MIN_DISPLAY_COUNT) return false;

    currentEntry = writePersistentCache(mergedPool, cardsByHandle);
    return true;
  }

  async function fetchCardHTML(handle) {
    if (!isValidProductHandle(handle)) return null;
    if (hasRecentCardMiss(handle)) return null;

    var url = '/products/' + encodeURIComponent(handle) + '?view=trending-card';

    try {
      var response = await fetch(url, {
        credentials: 'same-origin',
        headers: { Accept: 'text/html' },
      });
      if (response.status === 404 || response.status === 410) {
        rememberCardMiss(handle);
        return null;
      }
      if (!response.ok) return null;
      return await response.text();
    } catch (_) {
      return null;
    }
  }

  function buildCardElement(html, product, variant) {
    if (!html) return null;
    if (product && product.available === false) return null;

    var parser = new DOMParser();
    var doc = parser.parseFromString(html, 'text/html');
    var card = doc.querySelector('.productgrid--item');
    if (!card) return null;
    if (
      String(card.getAttribute('data-product-available') || '')
        .trim()
        .toLowerCase() === 'false'
    ) {
      if (product) {
        product.available = false;
      }
      return null;
    }

    card.classList.add('neu-trending-card');
    if (product.decisionToken) card.setAttribute("data-rec-decision", product.decisionToken);

    if (product && product.productId) {
      card.setAttribute('data-mm-rec-product-id', product.productId);
    }

    card.setAttribute('data-mm-rec-surface', variant || 'home');

    if (product && product.reasonSource) {
      card.setAttribute('data-rec-source', product.reasonSource);
    } else if (product && product.source) {
      card.setAttribute('data-rec-source', product.source);
    }

    return card;
  }

  function hashString(value) {
    var input = String(value || '');
    var hash = 2166136261;

    for (var i = 0; i < input.length; i += 1) {
      hash ^= input.charCodeAt(i);
      hash = Math.imul(hash, 16777619);
    }

    return hash >>> 0;
  }

  function shuffleWithSeed(items, seed) {
    var output = Array.isArray(items) ? items.slice() : [];
    var state = hashString(seed) || 1;

    function nextRandom() {
      state = Math.imul(state ^ (state >>> 15), 2246822519);
      state = Math.imul(state ^ (state >>> 13), 3266489917);
      state ^= state >>> 16;
      return (state >>> 0) / 4294967296;
    }

    for (var index = output.length - 1; index > 0; index -= 1) {
      var swapIndex = Math.floor(nextRandom() * (index + 1));
      var current = output[index];
      output[index] = output[swapIndex];
      output[swapIndex] = current;
    }

    return output;
  }

  function sameHandleSet(products, previousHandles) {
    if (!Array.isArray(products) || !Array.isArray(previousHandles) || products.length !== previousHandles.length) {
      return false;
    }

    var current = products
      .map(function (product) {
        return String((product && product.handle) || '').trim();
      })
      .filter(Boolean)
      .sort();
    var previous = previousHandles
      .map(function (handle) {
        return String(handle || '').trim();
      })
      .filter(Boolean)
      .sort();

    if (current.length !== previous.length) {
      return false;
    }

    for (var i = 0; i < current.length; i += 1) {
      if (current[i] !== previous[i]) {
        return false;
      }
    }

    return true;
  }

  function buildDisplayProducts(pool, rotation) {
    if (strictEligibility || (pool || []).some(function(p){ return p.algorithmVersion; })) return (pool || []).filter(function(p){return p.available !== false;}).slice(0, DISPLAY_LIMIT);
    var availablePool = (pool || []).filter(function (product) {
      return product && product.available !== false;
    });
    var personalized = availablePool.filter(function (product) {
      return product.source === 'personalized';
    });
    var fallback = availablePool.filter(function (product) {
      return product.source !== 'personalized';
    });
    var minimumFallback = fallback.length > 0 ? Math.min(3, fallback.length) : 0;
    var preferredPersonalizedCount = personalized.length > 0 ? Math.max(DISPLAY_LIMIT - minimumFallback, Math.ceil(DISPLAY_LIMIT * 0.7)) : 0;
    var selection = [];

    for (var attempt = 0; attempt < 4; attempt += 1) {
      var seedKey = [getCustomerKey(), getCustomerZone() || 'no-zone', rotation + attempt].join(':');
      var primary = shuffleWithSeed(personalized, seedKey + ':personalized');
      var secondary = shuffleWithSeed(fallback, seedKey + ':fallback');
      var combined = [];
      var seen = Object.create(null);

      function pushGroup(items) {
        for (var index = 0; index < items.length; index += 1) {
          var item = items[index];
          var handle = String((item && item.handle) || '').trim();
          if (!handle || seen[handle] || combined.length >= DISPLAY_LIMIT) continue;
          seen[handle] = true;
          combined.push(item);
        }
      }

      pushGroup(primary.slice(0, preferredPersonalizedCount));
      pushGroup(secondary.slice(0, Math.max(0, DISPLAY_LIMIT - combined.length)));
      pushGroup(primary);
      pushGroup(secondary);

      var candidate = shuffleWithSeed(combined, seedKey + ':display').slice(0, DISPLAY_LIMIT);

      if (!sameHandleSet(candidate, currentDisplayHandles) || availablePool.length <= DISPLAY_LIMIT) {
        selection = candidate;
        break;
      }
    }

    return selection;
  }

  function emitImpression(container, products) {
    if (!container || !products || products.length === 0 || typeof CustomEvent !== 'function') {
      return;
    }

    container.dispatchEvent(
      new CustomEvent('mm:recommendations:impression', {
        bubbles: true,
        detail: {
          variant: getRecommendationVariant(container),
          recommendations: products.map(function (product) {
            return {
              productId: product.productId,
              handle: product.handle,
              reason: product.reason,
              reasonSource: product.reasonSource || product.source || null,
            };
          }),
        },
      }),
    );
  }

  function setRefreshBusy(button, isBusy) {
    if (!button) return;
    var defaultLabel = button.getAttribute('data-default-label') || button.textContent || 'Refresh';
    button.setAttribute('data-default-label', defaultLabel);
    button.disabled = isBusy;
    button.textContent = isBusy ? 'Refreshing...' : defaultLabel;
  }

  async function ensureCardMarkup(products, cardsByHandle) {
    var missingHandles = [];

    for (var i = 0; i < products.length; i += 1) {
      var handle = products[i] && products[i].handle;
      if (!handle) continue;
      if (Object.prototype.hasOwnProperty.call(cardsByHandle, handle)) continue;
      missingHandles.push(handle);
    }

    if (missingHandles.length === 0) return false;

    var results = await Promise.all(
      missingHandles.map(function (handle) {
        return fetchCardHTML(handle).then(function (html) {
          return { handle: handle, html: html };
        });
      }),
    );

    for (var j = 0; j < results.length; j += 1) {
      cardsByHandle[results[j].handle] = results[j].html || null;
    }

    return true;
  }

  async function renderCurrentDisplay(container, options) {
    var config = options || {};

    if (!currentEntry || !Array.isArray(currentEntry.pool) || currentEntry.pool.length === 0) {
      if (!config.recoveredFromCatalog && (await recoverShortPoolWithCatalog())) {
        return renderCurrentDisplay(container, { recoveredFromCatalog: true });
      }
      if (renderFallbackDisplay(container)) return;
      container.innerHTML = '<div class="neu-browse__empty">No recommendations right now</div>';
      return;
    }

    var displayProducts = buildDisplayProducts(currentEntry.pool, currentRotation);
    if (displayProducts.length < (strictEligibility ? 1 : MIN_DISPLAY_COUNT)) {
      if (!config.recoveredFromCatalog && (await recoverShortPoolWithCatalog())) {
        displayProducts = buildDisplayProducts(currentEntry.pool, currentRotation);
      }
      if (displayProducts.length < (strictEligibility ? 1 : MIN_DISPLAY_COUNT)) {
        if (renderFallbackDisplay(container)) return;
        container.innerHTML = '<div class="neu-browse__empty">No recommendations right now</div>';
        return;
      }
    }

    var cardsByHandle = currentEntry.cardsByHandle && typeof currentEntry.cardsByHandle === 'object' ? currentEntry.cardsByHandle : {};
    var variant = getRecommendationVariant(container);
    var changed = await ensureCardMarkup(displayProducts, cardsByHandle);

    if (changed) {
      currentEntry = writePersistentCache(currentEntry.pool, cardsByHandle);
    }

    container.innerHTML = '';

    var rendered = 0;
    var prunedUnavailable = false;
    for (var i = 0; i < displayProducts.length; i += 1) {
      var product = displayProducts[i];
      var card = buildCardElement(cardsByHandle[product.handle], product, variant);
      if (!card) {
        if (product && product.available === false) {
          prunedUnavailable = true;
        }
        continue;
      }
      container.appendChild(card);
      rendered += 1;
    }

    if (prunedUnavailable && currentEntry && Array.isArray(currentEntry.pool)) {
      currentEntry = writePersistentCache(dedupeProducts(currentEntry.pool), cardsByHandle);
    }

    if (rendered < (strictEligibility ? 1 : MIN_DISPLAY_COUNT)) {
      if (!config.recoveredFromCatalog && (await recoverShortPoolWithCatalog())) {
        return renderCurrentDisplay(container, { recoveredFromCatalog: true });
      }
      if (renderFallbackDisplay(container)) return;
      container.innerHTML = '<div class="neu-browse__empty">No recommendations right now</div>';
      return;
    }

    if (rendered === 0) {
      container.innerHTML = '<div class="neu-browse__empty">No recommendations right now</div>';
      return;
    }

    currentDisplayHandles = displayProducts.map(function (product) {
      return product.handle;
    });
    emitImpression(container, displayProducts);

    if (typeof CustomEvent === 'function') {
      container.dispatchEvent(new CustomEvent('mm:cards:added', { bubbles: true }));
      container.dispatchEvent(
        new CustomEvent('theme:rimg:watch', {
          bubbles: true,
          cancelable: true,
        }),
      );
    }

    if (window.rimg && typeof window.rimg.watch === 'function') {
      try {
        window.rimg.watch(container);
      } catch (_) {}
    }

    window.setTimeout(function () {
      var lazyImgs = container.querySelectorAll('img[data-rimg="lazy"]');
      for (var index = 0; index < lazyImgs.length; index += 1) {
        var img = lazyImgs[index];
        if (img.getAttribute('data-rimg') === 'lazy' && img.src) {
          img.setAttribute('srcset', '');
          img.removeAttribute('srcset');
          img.setAttribute('data-rimg', 'loaded');
        }
      }
    }, 220);
  }

  async function hydrateTrending(options) {
    var config = options || {};
    var container = document.querySelector(CONTAINER_SELECTOR);
    if (!container) return;

    preserveFallbackMarkup(container);

    if (!config.skipSkeleton && !container.__mmRecommendedFallbackMarkup) {
      container.innerHTML = buildSkeletonCards(SKELETON_COUNT);
    }

    var entry = !config.force ? getCachedEntry() : null;
    if (!entry) {
      var pool = await fetchRecommendedPool();
      entry = writePersistentCache(pool, {});
    }

    currentEntry = entry;
    await renderCurrentDisplay(container);
    container.setAttribute('data-mm-recommended-hydrated', 'true');
  }

  function startHydration(options) {
    var config = options || {};
    var container = document.querySelector(CONTAINER_SELECTOR);
    if (!container) return Promise.resolve();
    if (!config.force && container.getAttribute('data-mm-recommended-hydrated') === 'true') {
      return Promise.resolve();
    }
    if (hydrationPromise) return hydrationPromise;

    hydrationPromise = hydrateTrending(config).finally(function () {
      hydrationPromise = null;
    });

    return hydrationPromise;
  }

  function bindRefresh(container) {
    var button = document.querySelector(REFRESH_SELECTOR);
    if (!button || button.__mmTrendingBound) return;

    button.__mmTrendingBound = true;
    button.addEventListener('click', function () {
      setRefreshBusy(button, true);

      var shouldForceFetch = !currentEntry || (strictEligibility && currentEntry.expiresAt <= Date.now()) || !Array.isArray(currentEntry.pool) || currentEntry.pool.length <= DISPLAY_LIMIT;
      if (!shouldForceFetch) {
        currentRotation += 1;
      } else {
        clearPersistentCache();
        currentRotation += 1;
      }

      Promise.resolve(shouldForceFetch ? hydrateTrending({ force: true, skipSkeleton: true }) : renderCurrentDisplay(container)).finally(function () {
        setRefreshBusy(button, false);
      });
    });
  }

  function init() {
    var container = document.querySelector(CONTAINER_SELECTOR);
    if (!container) return;

    bindRefresh(container);
    var cached = getCachedEntry();
    if (cached) {
      currentEntry = cached;
      void renderCurrentDisplay(container).then(function () {
        container.setAttribute('data-mm-recommended-hydrated', 'true');
      });
      return;
    }

    if ('IntersectionObserver' in window) {
      var observer = new IntersectionObserver(
        function (entries) {
          for (var i = 0; i < entries.length; i += 1) {
            if (!entries[i].isIntersecting) continue;
            observer.disconnect();
            void startHydration({ force: false });
            break;
          }
        },
        { rootMargin: INTERSECTION_ROOT_MARGIN },
      );
      observer.observe(container);
    } else {
      void startHydration({ force: false });
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }
})();
